import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, HelpCircle, Loader2 } from 'lucide-react';
import { useBranding } from '@/contexts/BrandingContext';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import HelpCenterCard, { HelpCenterCardData } from './HelpCenterCard';
import HelpCenterViewModal from './HelpCenterViewModal';
import HelpCenterEditModal from './HelpCenterEditModal';

interface HelpCenterCardFromDB {
  id: number;
  partner_id: string;
  title: string;
  description?: string;
  content: string;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

// Sortable wrapper for HelpCenterCard
function SortableHelpCenterCard({
  card,
  onEdit,
  onDelete,
  onView
}: {
  card: HelpCenterCardFromDB;
  onEdit: (card: HelpCenterCardData) => void;
  onDelete: (id: number) => void;
  onView: (card: HelpCenterCardData) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <HelpCenterCard
        id={card.id}
        title={card.title}
        description={card.description}
        content={card.content}
        display_order={card.display_order}
        is_published={card.is_published}
        created_at={card.created_at}
        updated_at={card.updated_at}
        mode="admin"
        onEdit={onEdit}
        onDelete={onDelete}
        onView={onView}
        dragHandleProps={listeners}
      />
    </div>
  );
}

function HelpCenterManager() {
  const { isWhiteLabel } = useBranding();
  const [cards, setCards] = useState<HelpCenterCardFromDB[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<HelpCenterCardData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load cards
  const loadCards = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('help-center-cards', {
        method: 'GET',
      });

      if (error) throw error;
      setCards(data?.cards || []);
    } catch (error) {
      console.error('Error loading cards:', error);
      toast.error('Failed to load help center cards');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isWhiteLabel) {
      loadCards();
    }
  }, [isWhiteLabel]);

  // Create new card
  const handleCreateCard = () => {
    setSelectedCard(null);
    setEditModalOpen(true);
  };

  // Edit existing card
  const handleEditCard = (card: HelpCenterCardData) => {
    setSelectedCard(card);
    setEditModalOpen(true);
  };

  // View card
  const handleViewCard = (card: HelpCenterCardData) => {
    setSelectedCard(card);
    setViewModalOpen(true);
  };

  // Delete card
  const handleDeleteCard = async (id: number) => {
    if (!confirm('Are you sure you want to delete this card?')) return;

    try {
      const { error } = await supabase.functions.invoke('help-center-cards', {
        method: 'DELETE',
        body: { id },
      });

      if (error) throw error;

      toast.success('Card deleted successfully');
      loadCards();
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error('Failed to delete card');
    }
  };

  // Save card (create or update)
  const handleSaveCard = async (cardData: { title: string; description?: string; content: string; is_published: boolean }) => {
    setIsSaving(true);
    try {
      const method = selectedCard ? 'PUT' : 'POST';
      const body = selectedCard
        ? { id: selectedCard.id, ...cardData }
        : cardData;

      const { error } = await supabase.functions.invoke('help-center-cards', {
        method,
        body,
      });

      if (error) throw error;

      toast.success(selectedCard ? 'Card updated successfully' : 'Card created successfully');
      setEditModalOpen(false);
      loadCards();
    } catch (error) {
      console.error('Error saving card:', error);
      toast.error('Failed to save card');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = cards.findIndex((card) => card.id === active.id);
      const newIndex = cards.findIndex((card) => card.id === over.id);

      const newCards = arrayMove(cards, oldIndex, newIndex);

      // Update display_order for all affected cards
      const cardOrders = newCards.map((card, index) => ({
        id: card.id,
        display_order: index,
      }));

      setCards(newCards);

      try {
        const { error } = await supabase.functions.invoke('help-center-cards', {
          method: 'PATCH',
          body: { cardOrders },
        });

        if (error) throw error;
      } catch (error) {
        console.error('Error reordering cards:', error);
        toast.error('Failed to reorder cards');
        // Revert the change
        loadCards();
      }
    }
  };

  if (!isWhiteLabel) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <HelpCircle className="h-5 w-5 mr-2" />
              Help Center Editor
            </CardTitle>
            <CardDescription>
              Help center editing is only available for white-label partners
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HelpCircle className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                White-Label Feature
              </h3>
              <p className="text-gray-600 mb-4">
                Upgrade to a white-label partnership to edit your help center cards.
              </p>
              <Button variant="outline">
                Contact Sales
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading help center cards...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <HelpCircle className="h-5 w-5 mr-2" />
                Help Center Editor
              </CardTitle>
              <CardDescription>
                Create and edit help center cards for your clients. Cards are displayed in order and can be published or kept as drafts.
              </CardDescription>
            </div>
            <Button onClick={handleCreateCard}>
              <Plus className="h-4 w-4 mr-2" />
              Create New Card
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {cards.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No help center cards yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first help center card to get started.
              </p>
              <Button onClick={handleCreateCard}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Card
              </Button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={cards.map(card => card.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {cards.map((card) => (
                    <SortableHelpCenterCard
                      key={card.id}
                      card={card}
                      onEdit={handleEditCard}
                      onDelete={handleDeleteCard}
                      onView={handleViewCard}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <HelpCenterViewModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        card={selectedCard}
        mode="admin"
      />

      <HelpCenterEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        card={selectedCard}
        onSave={handleSaveCard}
        isLoading={isSaving}
      />
    </div>
  );
}

export default HelpCenterManager;